import { ChevronDown } from 'react-feather'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { useRef, useState, useEffect } from 'react'
import type React from 'react'
import DesktopTimePicker from '@/app/components/DesktopTimePicker/DesktopTimePicker'
import useIsMobile from '../hooks/useIsMobile'
import twMerge from '@/utils/classes/twMerge'

const DEFAULT_HOUR = 9
const DEFAULT_MINUTE = 0

interface SmartTimePickerProps {
  onChange?: (time: { hour: number; minute: number }) => void
  hour?: number
  minute?: number
  className?: string
  showPlusOne?: boolean
}

const SmartTimePicker = ({
  onChange: onChange,
  hour,
  minute,
  className,
  showPlusOne = false,
}: SmartTimePickerProps) => {
  const [desktopTimePickerOpen, setDesktopTimePickerOpen] = useState(false)
  const ref = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()

  const value = dayjs()
    .set('hours', hour ?? DEFAULT_HOUR)
    .set('minutes', minute ?? DEFAULT_MINUTE)

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        ref.current &&
        !ref.current.contains(event.target as Node)
      ) {
        setDesktopTimePickerOpen(false)
      }
    }

    if (desktopTimePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [desktopTimePickerOpen])

  const desktopOnChange = (date: Dayjs) => {
    if (!onChange) {
      return
    }
    onChange({ hour: date.hour(), minute: date.minute() })
  }

  const mobileOnChange = ({
    currentTarget,
  }: React.ChangeEvent<HTMLInputElement>) => {
    let hour = DEFAULT_HOUR
    let minute = DEFAULT_MINUTE
    if (currentTarget?.value && currentTarget.value.includes(':')) {
      hour = parseInt(currentTarget.value.split(':')[0])
      minute = parseInt(currentTarget.value.split(':')[1])
    }
    if (onChange) {
      onChange({ hour, minute })
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div
        className={twMerge(
          'relative pl-1 py-1 bg-themed-blue-100 rounded-[10px] m-0 h-8',
          className,
        )}
      >
        <div className="relative flex items-center h-full">
          <input
            id="time"
            type="time"
            value={value.format('HH:mm')}
            ref={ref}
            onClick={(e) => {
              if (!isMobile) {
                e.preventDefault()
                setDesktopTimePickerOpen(true)
              }
            }}
            onChange={mobileOnChange}
            style={{ fontFamily: 'inherit' }}
            className="text-xs text-themed-blue-500 font-bold bg-themed-blue-100 pl-0.5 border-none outline-none h-full flex-1 appearance-none tracking-tight [-webkit-appearance:none] [-moz-appearance:textfield] [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
          />
          {showPlusOne && (
            <span className="text-xs text-blue-500 font-mono pr-1 -ml-1">
              ⁺¹
            </span>
          )}
          <ChevronDown
            className="pointer-events-none text-themed-blue-500 mr-1"
            size={12}
          />
        </div>
        {desktopTimePickerOpen && (
          <div
            ref={dropdownRef}
            className="absolute z-50 mt-1 bg-white rounded-lg shadow-lg border left-0 top-full"
          >
            <div className="p-4">
              <DesktopTimePicker
                defaultValue={value}
                onChange={(value: Dayjs) => {
                  desktopOnChange(value)
                  setDesktopTimePickerOpen(false)
                  return value
                }}
              />
            </div>
          </div>
        )}
      </div>
    </LocalizationProvider>
  )
}

export default SmartTimePicker
